import { Component, OnInit, OnDestroy, Input } from '@angular/core';
import { ComparisonDirectionEnum, ComparisonResultEnum, GaitComparisonViewModel, RunningComparisonReportViewModels} from 'src/app/_models/generatedModels';
import { ActivatedRoute, Router } from '@angular/router';
import { AssessmentService } from 'src/app/_services/generatedServices';
import { SettingsProvider } from 'src/app/_services/settingsProvider.service';
import { ToasterService } from 'src/app/_services/toaster.service';
import { Enums } from '../../_models/generatedEnums';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { AuthenticationService } from '../../_services/authentication.service';
import { HttpErrorResponse } from '@angular/common/http';


@Component({
  selector: 'running-comparison-report',
  templateUrl: './running-comparison-report.component.html',
  styleUrls: ['./running-comparison-report.component.scss']
})
export class RunningComparisonReportComponent implements OnInit, OnDestroy
{
  initialized = false;
  firstReportId: any;
  secondReportId: any;
  comparisonModel: RunningComparisonReportViewModels;
  shareableLink: string;
  categoryScoreEnum = Enums.CategoryScoreEnum;
  AssessmentResultShortEnum = Enums.AssessmentResultShortEnum;
  gaitScoreDescriptionEnum = Enums.GaitScoreDescriptionEnum;
  runningAnalysisProgramPrimaryFactorEnum = Enums.RunningAnalysisProgramPrimaryFactorEnum;
  impairmentCategoryEnum = Enums.ImpairmentCategoryEnum;
  runningAnalysisReportScoreDescriptionEnum = Enums.RunningAnalysisReportScoreDescriptionEnum;
  footStrikeEnum = Enums.FootStrikePatternEnum;
  speedUnitEnum = Enums.SpeedUnitEnum;

  assessmentsUrl: string = './../../../../analysis-running/view'; 

  @Input()
  isShared: boolean = false;

  constructor(private route: ActivatedRoute, private assessmentData: AssessmentService, private auth: AuthenticationService, private settingsProvider: SettingsProvider, private toastr: ToasterService, private modalService: NgbModal, private router: Router) { }

  ngOnInit(): void
  {
    this.firstReportId = this.route.snapshot.params ? this.route.snapshot.params['rid'] : null;
    this.secondReportId = this.route.snapshot.params ? this.route.snapshot.params['secrid'] : null;

    let pattern = /^[0-9a-fA-F]{8}\b-[0-9a-fA-F]{4}\b-[0-9a-fA-F]{4}\b-[0-9a-fA-F]{4}\b-[0-9a-fA-F]{12}$/gi;
    let isFirstReportIdGuid = this.firstReportId.match(pattern) ? true : false;
    let isSeconfReportIdGuid = this.secondReportId.match(pattern) ? true : false;

    if (isFirstReportIdGuid && isSeconfReportIdGuid && this.isShared) {
      const model = new GaitComparisonViewModel();
      model.firstReportGuidId = this.firstReportId;
      model.secondReportGuidId = this.secondReportId;

      this.assessmentData.getRunningComparisonReportByGuid(model).subscribe((result) =>
      {
        this.comparisonModel = result;
        this.initialized = true;
      });
    } else {
      this.assessmentData.getRunningComparisonReport(this.firstReportId, this.secondReportId).subscribe((result) =>
      {
        this.comparisonModel = result;
        this.shareableLink = this.settingsProvider.siteUrl + `/running-comparison/${this.comparisonModel.olderRunningReport.guidId}/${this.comparisonModel.newerRunningReport.guidId}`;

        this.initialized = true;
      },
        (err: HttpErrorResponse) =>
        {
          if (err && err.error && err.error.indexOf('UnauthorizedAccessException') >= 0) {
            this.toastr.error('You do not have access to all or some compared assessments', 'Error',);
          } else {
            this.toastr.error('An error occurred while loading the assessment', 'Error');
          }

          this.router.navigate(['../../../../'], { relativeTo: this.route });

        });
    }
  }

  getDiffStyle(difference: number)
  {
    if(difference > 0)
      return "cs_green";

    if (difference < 0)
      return "cs_red";

    return "";
  }

  getGradientPosition(minValue, maxValue, averageValue, standardDeviation, stDeviationsCount)
  {
    // Calculate the value based on the number of standard deviations
    const value = averageValue + stDeviationsCount * standardDeviation;

    // Ensure the value doesn't go below the minValue or above the maxValue
    const clampedValue = Math.max(minValue, Math.min(maxValue, value));

    // Calculate the percentage position of the value within the range
    const position = ((clampedValue - minValue) / (maxValue - minValue)) * 100;

    if (position > 97)
      return 97;

    if (position < 3)
      return 3;

    return position;
  }

  generateLinearGradient(minValue: number, maxValue: number, averageValue: number, standardDeviation: number): string
  {
    // Calculate the key thresholds
    const greenStart = averageValue - standardDeviation; // Start of green area
    const greenEnd = averageValue + standardDeviation;   // End of green area

    const yellowStart1 = greenStart - standardDeviation; // Start of first yellow area
    const yellowEnd1 = greenStart;                       // End of first yellow area
    const yellowStart2 = greenEnd;                       // Start of second yellow area
    const yellowEnd2 = greenEnd + standardDeviation;     // End of second yellow area

    const redEnd1 = yellowStart1;                        // End of first red area
    const redStart2 = yellowEnd2;                        // Start of second red area

    // Calculate percentages for the gradient stops
    const percentage = (value) => ((value - minValue) / (maxValue - minValue)) * 100;

    const redEnd1Percent = percentage(redEnd1);
    const yellowStart1Percent = percentage(yellowStart1);
    const yellowEnd1Percent = percentage(yellowEnd1);
    const greenStartPercent = percentage(greenStart);
    const greenEndPercent = percentage(greenEnd);
    const yellowStart2Percent = percentage(yellowStart2);
    const yellowEnd2Percent = percentage(yellowEnd2);
    const redStart2Percent = percentage(redStart2);

    const redMiddle = (redEnd1Percent) / 2;
    const yellowMiddle = yellowStart1Percent + (yellowEnd1Percent - redEnd1Percent) / 2;
    const greenEnd1Middle = greenStartPercent + (greenEndPercent - yellowEnd1Percent) / 2;
    const yellow2Middle = yellowStart2Percent + (yellowEnd2Percent - greenEndPercent) / 2;
    const red2Middle = redStart2Percent + (100 - yellowEnd2Percent) / 2;

    // Generate the CSS linear-gradient value
    return `linear-gradient(90deg, 
    var(--red) ${redMiddle}%, 
    var(--yellow) ${yellowMiddle}%, 
    var(--green) ${greenEnd1Middle}%, 
    var(--yellow) ${yellow2Middle}%, 
    var(--red) ${red2Middle}%)`;
  }


  onCopyLink()
  {
    //copy link to clipboard
    let link = document.createElement('textarea');
    link.value = this.shareableLink;
    document.body.appendChild(link);
    link.select();
    document.execCommand('copy');
    document.body.removeChild(link);
    this.toastr.success('Link copied to clipboard', 'Success');
  }

  getNumberFromNumber(value: string)
  {
    if (!value)
      return 0;

    return parseInt(value);
  }

  ngOnDestroy(): void
  {
  }
}
